// material-ui
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

// project imports
import CircularWithPath from "components/@extended/progress/CircularWithPath";
import { openSnackbar } from "api/snackbar";
// types
import { SnackbarProps } from "types/snackbar";
import { useAppDispatch } from "hooks";
import { BasicInfo } from "model/basicInfo";
import { addJobAva, editJobAva } from "store/actions/main/basicInfo/jobAva";
import { FormControlLabel } from "@mui/material";
import { Switch } from "@mui/material";
import { FormHelperText } from "@mui/material";
import { CircularProgress } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";

// constant
const getInitialValues = (customer: BasicInfo | null) => {
  if (customer) {
    return {
      name: customer.name || "",
      order: customer.order || 0,
      name_trans: customer.name_trans.ar || "",
      status: customer.status || false,
    };
  } else {
    return {
      name: "",
      order: 0,
      name_trans: "",
      status: false,
    };
  }
};

// ==============================|| CUSTOMER ADD / EDIT - FORM ||============================== //

const FormJobAvaAdd = ({
  customer,
  id,
  closeModal,
  fetchData,
  loading,
}: {
  loading: any;
  setLoading: any;
  fetchData: any;
  id: number;
  customer: BasicInfo | null;
  closeModal: () => void;
}) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const CustomerSchema = Yup.object().shape({
    name: Yup.string().required("English Name is required"),
    name_trans: Yup.string().required("Arabic Name is required"),
    order: Yup.number().required("Order is required"),
  });

  const formik = useFormik({
    initialValues: getInitialValues(customer!),
    validationSchema: CustomerSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (customer) {
          await dispatch(
            editJobAva({
              id: customer.id || 0,
              data: {
                name: values.name,
                name_trans: {
                  ar: values.name_trans,
                },

                order: values.order,
                status: values.status,
              },
            })
          ).then((action) => {
            fetchData();
            if (action.type === "basicInfo/editJobAva/fulfilled") {
              openSnackbar({
                open: true,
                message: "Job Availability edited successfully",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: {
                  color: "success",
                },
              } as SnackbarProps);
              setSubmitting(false);
              closeModal();
            }
          });
        } else {
          // Dispatch create newCat action if no customer exists
          await dispatch(
            addJobAva({
              name: values.name,
              name_trans: {
                ar: values.name_trans,
              },

              order: values.order,
              status: values.status,
            })
          ).then((action) => {
            fetchData();
            if (action.type === "basicInfo/addJobAva/fulfilled") {
              openSnackbar({
                open: true,
                message: "Job Availability added successfully",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: {
                  color: "success",
                },
              } as SnackbarProps);
              setSubmitting(false);
              closeModal();
            }
          });
        }
        closeModal();
      } catch (error) {
        console.error(error);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  if (loading)
    return (
      <Box sx={{ p: 5 }}>
        <Stack direction="row" justifyContent="center">
          <CircularWithPath />
        </Stack>
      </Box>
    );

  return (
    <>
      <FormikProvider value={formik}>
        <LocalizationProvider>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle
              style={{
                textAlign: "center",
                fontSize: "22px",
                fontWeight: "bold",
                color: "#C49914",
                marginBottom: "10px",
              }}
            >
              {customer ? (
                <FormattedMessage id="Edit Job Availability" />
              ) : (
                <FormattedMessage id="Add Job Availability" />
              )}
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 2.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={5} mb={1}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-name">
                      <FormattedMessage id="English Name" />{" "}
                      <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="customer-name"
                      placeholder={intl.formatMessage({
                        id: "Enter English Name",
                        defaultMessage: "Enter English Name",
                      })}
                      {...getFieldProps("name")}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Stack>
                </Grid>
                <Grid item md={1} />
                <Grid item xs={12} md={5} mb={1}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-name">
                      <FormattedMessage id="Arabic Name" />{" "}
                      <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="customer-name"
                      name="name_trans"
                      {...getFieldProps("name_trans")}
                      placeholder={intl.formatMessage({
                        id: "Enter Arabic Name",
                        defaultMessage: "Enter Arabic Name",
                      })}
                      error={Boolean(touched.name_trans && errors.name_trans)}
                      helperText={touched.name_trans && errors.name_trans}
                    />
                  </Stack>
                </Grid>
                <Grid md={1} />
                <Grid item xs={12} sm={5}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-order">
                      <FormattedMessage id="Order" />{" "}
                      <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      type="number"
                      id="customer-order"
                      defaultValue={0}
                      placeholder={intl.formatMessage({
                        id: "enter.order",
                        defaultMessage: "Enter Order",
                      })}
                      {...getFieldProps("order")}
                      error={Boolean(touched.order && errors.order)}
                      helperText={touched.order && errors.order}
                    />
                  </Stack>
                </Grid>
                <Grid md={1} />
                <Grid item xs={12} md={5}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-is_active">
                      <FormattedMessage id="Status" />
                    </InputLabel>
                    <FormControl fullWidth>
                      <FormControlLabel
                        control={
                          <Switch
                            id="customer-is_active"
                            checked={formik.values.status}
                            {...getFieldProps("status")}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setFieldValue("status", checked);
                            }}
                          />
                        }
                        label={
                          formik.values.status ? (
                            <FormattedMessage id="Active" />
                          ) : (
                            <FormattedMessage id="Inactive" />
                          )
                        }
                      />
                    </FormControl>
                    {touched.status && errors.status && (
                      <FormHelperText
                        error
                        id="standard-weight-helper-text-email-login"
                        sx={{ pl: 1.75 }}
                      >
                        {errors.status}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={4}>
                    {customer ? (
                      <Button fullWidth type="submit" variant="contained">
                        {isSubmitting ? (
                          <CircularProgress
                            size="1.5rem"
                            style={{ color: "white" }}
                          />
                        ) : (
                          <FormattedMessage id="Update" />
                        )}
                      </Button>
                    ) : (
                      <Button fullWidth type="submit" variant="contained">
                        {isSubmitting ? (
                          <CircularProgress
                            size="1.5rem"
                            style={{ color: "white" }}
                          />
                        ) : (
                          <FormattedMessage id="Submit" />
                        )}
                      </Button>
                    )}
                  </Grid>
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={closeModal}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </LocalizationProvider>
      </FormikProvider>
    </>
  );
};

export default FormJobAvaAdd;
